"use server";

import {
  BrandExtraMetadata,
  SimulatedTransactionDetails,
  VoucherProduct,
  SimulateTransactionRequest,
} from "~/server/types/brand";
import { getStaticProxyJson } from "~/server/static";
import { serverRepo } from "~/server/apis/server_repo";
import {
  defaultRequestForADenominationsBrand,
  defaultRequestForAVariantBrand,
} from "~/utils/webstore/default_simulate_request";
import { InitialSimulationData } from "~/hooks/denominations_grid/useDenominationGridState";

type FAQObject = {
  "@type": "Question";
  name: string;
  acceptedAnswer: {
    "@type": "Answer";
    text: string;
  };
};

type SEOSchema = {
  alternateName: string;
  sameAs: string[];
  keywords: string;
  disambiguatingDescription: string;
  faqObjs: FAQObject[];
};

type MetadataSEOSchema = Record<string, SEOSchema>;

export type BrandL2WebstoreTelescopeRouteData = {
  brand: VoucherProduct;
  brandExtraMetadata: BrandExtraMetadata;
  simulate: InitialSimulationData;
  similarBrands: VoucherProduct[];
  metadataSEOSchema?: MetadataSEOSchema;
};

export const getBrandL2WebstoreTelescopeRouteData = async (
  brandKey: string
): Promise<BrandL2WebstoreTelescopeRouteData> => {
  // Fetch brand data and metadata in parallel
  const brand$P = serverRepo.getBrand(brandKey);
  const brandExtraMetadata$P = serverRepo.getBrandExtraMetadata(brandKey);
  const metadataSEOSchema$P = getStaticProxyJson(
    "brandl2_metadata_schema.json"
  );

  const resolved = await Promise.all([
    brand$P,
    brandExtraMetadata$P,
    metadataSEOSchema$P,
  ]);

  const brand = resolved[0];
  const brandExtraMetadata = resolved[1];
  const metadataSEOSchema = resolved[2];

  // TODO: Fetch similar brands when API is available
  // For now, return empty array
  const similarBrands: VoucherProduct[] = [];

  // Prepare simulation
  const isVariantBrand = brand.variantDetailedResponse != null;
  const isDenominationsBrand = brand.amountConditions.denominations != null;
  let simulatedTransactionDetails: SimulatedTransactionDetails | null = null;
  let simulateError: string | null = null;
  let requestedAmount: number | null = null;
  let requestedVariantId: string = "";

  try {
    let simulateRequest: SimulateTransactionRequest = {
      voucherProductId: brand.externalId,
      amount: brand.amountConditions.defaultAmount,
      consumeCoins: false, // Webstore-only: no coin consumption by default
    };

    if (isVariantBrand) {
      simulateRequest = {
        ...simulateRequest,
        ...defaultRequestForAVariantBrand(brand),
      };
    } else if (isDenominationsBrand) {
      simulateRequest = {
        ...simulateRequest,
        ...defaultRequestForADenominationsBrand(brand),
      };
    }

    // Save requested values for InitialSimulationData
    requestedAmount = simulateRequest.amount;
    requestedVariantId = (simulateRequest as any).variantId || "";

    const simulateResponse = await serverRepo.simulate(simulateRequest);

    // Filter out wallet payment route
    const filteredSimulations = simulateResponse.data.filter(
      (data) => data.paymentRoute !== "WALLET"
    );
    simulatedTransactionDetails = filteredSimulations[0] || null;
  } catch (error: any) {
    console.error("Simulation error for brand:", brandKey, error.message);
    simulateError = error.message;
  }

  // Transform simulation data to InitialSimulationData format
  const simulate: InitialSimulationData = {
    selectedAmount: requestedAmount,
    selectedVariantId: requestedVariantId,
    dueAmount: simulatedTransactionDetails?.dueAmount ?? null,
    discountDetails: simulatedTransactionDetails?.discountDetails ?? null,
    redemptionAmount: simulatedTransactionDetails?.redemptionAmount ?? null,
    dueAmount2: null,
    discountDetails2: null,
    redemptionAmount2: null,
    hubbleCoinRewards:
      simulatedTransactionDetails && brand.rewardType === "POSTPAID"
        ? {
            amount:
              simulatedTransactionDetails.discountDetails.totalDiscount.amount -
              simulatedTransactionDetails.discountDetails.coinDiscount.amount,
            percentage:
              simulatedTransactionDetails.discountDetails.totalDiscount
                .percentage -
              simulatedTransactionDetails.discountDetails.coinDiscount
                .percentage,
          }
        : null,
    initialErrorOnSimulate: simulateError,
    offerConstructTenure: null,
  };

  return {
    brand,
    brandExtraMetadata,
    simulate,
    similarBrands,
    metadataSEOSchema,
  };
};
